import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AdminLeads.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = ['new', 'texted', 'call booked', 'joined', 'not right now'];

const formatPT = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (e) { return iso; }
};

const AdminLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('ri_admin_token') : null;

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { try { document.head.removeChild(meta); } catch (e) {} };
  }, []);

  const fetchLeads = useCallback(async () => {
    if (!token) { navigate('/admin/login'); return; }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const { data } = await axios.get(`${API}/admin/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setNewCount(data.new_count || 0);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('ri_admin_token');
        navigate('/admin/login');
        return;
      }
      setError('failed to load leads. try again.');
    } finally {
      setLoading(false);
    }
  }, [token, search, statusFilter, navigate]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateLead = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      // refresh counts
      fetchLeads();
    } catch (err) {
      alert('update failed');
    }
  };

  const onLogout = () => {
    localStorage.removeItem('ri_admin_token');
    navigate('/admin/login');
  };

  const onExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', 'run-it-leads.csv');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('export failed');
    }
  };

  return (
    <div className="al-page">
      <main className="al-main">
        <header className="al-header">
          <div>
            <h1 className="al-h">run it · leads</h1>
            <p className="al-sub">
              <strong data-testid="al-total">{total}</strong> total · <strong data-testid="al-new">{newCount}</strong> new
            </p>
          </div>
          <div className="al-header-actions">
            <button type="button" className="al-btn al-btn-outline" onClick={onExport} data-testid="al-export">
              export CSV
            </button>
            <button type="button" className="al-btn al-btn-ghost" onClick={onLogout} data-testid="al-logout">
              sign out
            </button>
          </div>
        </header>

        <div className="al-controls">
          <input
            type="search"
            placeholder="search name or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="al-search"
            data-testid="al-search"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="al-filter"
            data-testid="al-filter"
          >
            <option value="all">all statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {error && <p className="al-error" data-testid="al-error">{error}</p>}
        {loading && <p className="al-loading">loading…</p>}

        <div className="al-table-wrap">
          <table className="al-table" data-testid="al-table">
            <thead>
              <tr>
                <th>submitted</th>
                <th>name</th>
                <th>phone</th>
                <th>what they want to change</th>
                <th>status</th>
                <th>notes</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && !loading && (
                <tr><td colSpan={6} className="al-empty">no leads yet.</td></tr>
              )}
              {leads.map((lead) => (
                <tr key={lead.id} data-testid={`al-row-${lead.id}`}>
                  <td data-label="submitted">{formatPT(lead.submitted_at)}</td>
                  <td data-label="name"><strong>{lead.name}</strong></td>
                  <td data-label="phone">
                    <a href={`sms:${lead.phone}`} className="al-phone-link">{lead.phone}</a>
                  </td>
                  <td data-label="answer" className="al-answer">{lead.answer}</td>
                  <td data-label="status">
                    <select
                      value={lead.status}
                      onChange={(e) => updateLead(lead.id, { status: e.target.value })}
                      className={`al-status al-status-${(lead.status || 'new').replace(/\s+/g, '-')}`}
                      data-testid={`al-status-${lead.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td data-label="notes">
                    <NotesCell
                      initial={lead.notes || ''}
                      onSave={(val) => updateLead(lead.id, { notes: val })}
                      testid={`al-notes-${lead.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

const NotesCell = ({ initial, onSave, testid }) => {
  const [val, setVal] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setVal(initial); setDirty(false); }, [initial]);
  return (
    <div className="al-notes">
      <textarea
        rows={2}
        value={val}
        onChange={(e) => { setVal(e.target.value); setDirty(e.target.value !== initial); }}
        data-testid={testid}
      />
      {dirty && (
        <button type="button" className="al-notes-save" onClick={() => onSave(val)}>save</button>
      )}
    </div>
  );
};

export default AdminLeads;
